/**
 * AskPage — Public AI Sales Chat
 * 
 * Public-facing page where prospects can chat with an AI sales assistant.
 * Reads initial question from URL param, styled to match the landing page.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Send, Sparkles, Loader2, ArrowLeft, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const generateSessionId = () => {
  const stored = localStorage.getItem('sr_ask_session');
  if (stored) return stored;
  const id = `pub_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem('sr_ask_session', id);
  return id;
};

const AskPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => generateSessionId());
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasSentInitial = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load existing history
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/chat/history?session_id=${sessionId}`);
        const data = await res.json();
        if (data.messages?.length > 0) {
          setMessages(data.messages);
        }
      } catch (err) {
        console.error('Failed to load history:', err);
      }
    };
    loadHistory();
  }, [sessionId]);

  const sendMessage = useCallback(async (text) => {
    if (!text?.trim() || loading) return;

    const userMsg = { role: 'user', content: text.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/public/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: text.trim(),
          referrer: document.referrer || null,
          user_agent: navigator.userAgent || null,
          utm_source: searchParams.get('utm_source'),
          utm_medium: searchParams.get('utm_medium'),
          utm_campaign: searchParams.get('utm_campaign'),
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString(),
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.detail || 'Sorry, something went wrong. Please try again.',
          timestamp: new Date().toISOString(),
        }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble connecting. Please try again in a moment.",
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [loading, sessionId, searchParams]);

  // Send initial question from sessionStorage (set by homepage)
  useEffect(() => {
    const q = sessionStorage.getItem('sr_ask_question');
    if (q && !hasSentInitial.current && messages.length === 0) {
      sessionStorage.removeItem('sr_ask_question');
      hasSentInitial.current = true;
      sendMessage(q);
    }
  }, [messages.length, sendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleNewChat = () => {
    localStorage.removeItem('sr_ask_session');
    window.location.href = '/ask';
  };

  // Markdown link handler — intercept /help/ links
  const markdownComponents = {
    a: ({ children }) => <span className="text-purple-400 font-medium">{children}</span>,
    p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
    ul: ({ children }) => <ul className="mb-3 pl-4 space-y-1 list-disc list-outside">{children}</ul>,
    ol: ({ children }) => <ol className="mb-3 pl-4 space-y-1 list-decimal list-outside">{children}</ol>,
    strong: ({ children }) => <strong className="text-purple-400 font-semibold">{children}</strong>,
    h3: ({ children }) => <h3 className="text-sm font-semibold text-white mt-3 mb-2">{children}</h3>,
    code: ({ children }) => <code className="bg-white/10 px-1.5 py-0.5 rounded text-sm">{children}</code>,
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans selection:bg-purple-500 selection:text-white flex flex-col" data-testid="ask-page">

      {/* Nav — matching landing page */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-black/80 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <span className="text-white font-bold text-sm">SR</span>
            </div>
            <span className="text-lg font-semibold text-white tracking-tight group-hover:text-gray-300 transition-colors">
              StackRanked.ai
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white hover:bg-white/5 gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-white text-black hover:bg-gray-200 font-medium"
            >
              Try Free
            </Button>
          </div>
        </div>
      </nav>

      {/* Main chat area */}
      <main className="flex-1 flex flex-col items-center pt-24 pb-4 px-4">
        {/* Chat container */}
        <div className="w-full max-w-3xl flex-1 flex flex-col min-h-0">

          {/* Empty state */}
          {messages.length === 0 && !loading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/20 flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="w-8 h-8 text-purple-400" />
                </div>
                <h1 className="text-2xl font-bold mb-3">Ask anything about StackRanked</h1>
                <p className="text-gray-500 max-w-md mx-auto">
                  Our AI knows every feature, workflow, and integration. Get instant answers.
                </p>

                {/* Quick action chips */}
                <div className="flex flex-wrap justify-center gap-2 mt-8">
                  {[
                    'What makes StackRanked different?',
                    'How does the AI work?',
                    'Compare to Productboard',
                  ].map((q) => (
                    <button
                      key={q}
                      data-testid={`quick-action-${q.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => sendMessage(q)}
                      className="px-4 py-2 rounded-full border border-white/10 bg-white/5 text-sm text-gray-300 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          {(messages.length > 0 || loading) && (
            <div className="flex-1 overflow-y-auto py-4 space-y-6" data-testid="chat-messages">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/20 flex items-center justify-center mr-3 mt-1">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] rounded-2xl px-5 py-3.5 ${
                      msg.role === 'user'
                        ? 'bg-purple-600/80 text-white'
                        : 'bg-white/[0.04] border border-white/[0.06] text-gray-300'
                    }`}
                    data-testid={`chat-message-${msg.role}`}
                  >
                    {msg.role === 'assistant' ? (
                      <div className="prose prose-sm prose-invert max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-[15px] leading-relaxed">{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {loading && (
                <div className="flex justify-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/20 flex items-center justify-center mr-3">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                  </div>
                  <div className="bg-white/[0.04] border border-white/[0.06] rounded-2xl px-5 py-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-purple-400/60 animate-bounce [animation-delay:0ms]" />
                      <div className="w-2 h-2 rounded-full bg-purple-400/60 animate-bounce [animation-delay:150ms]" />
                      <div className="w-2 h-2 rounded-full bg-purple-400/60 animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input area — pinned to bottom */}
          <div className="pt-4 pb-2">
            {messages.length > 0 && (
              <div className="flex justify-center mb-3">
                <button
                  onClick={handleNewChat}
                  className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                  data-testid="new-chat-button"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  New conversation
                </button>
              </div>
            )}
            <div className="relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about features, pricing, integrations..."
                rows={1}
                disabled={loading}
                data-testid="ask-chat-input"
                className="w-full bg-white/[0.04] border border-white/10 rounded-2xl pl-5 pr-14 py-4 text-[15px] text-white placeholder-gray-600 resize-none overflow-hidden focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-all disabled:opacity-50"
                style={{ minHeight: '56px', maxHeight: '160px' }}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                }}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading}
                data-testid="ask-send-button"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:bg-white/5 disabled:text-gray-600 flex items-center justify-center transition-all"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className="text-center text-xs text-gray-600 mt-3">
              Powered by StackRanked AI. Responses are based on our documentation.
            </p>
          </div>
        </div>
      </main>

      {/* Minimal footer */}
      <footer className="py-6 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto flex items-center justify-between text-xs text-gray-600">
          <span>© 2025 StackRanked.ai</span>
          <div className="flex items-center gap-4">
            <a href="/terms" className="hover:text-gray-400 transition-colors">Terms</a>
            <a href="/privacy" className="hover:text-gray-400 transition-colors">Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AskPage;
